import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceArea,
  ResponsiveContainer,
  Customized,
  Tooltip,
} from 'recharts';

import DropdownSelect from '../Visualeffects/DropdownSelect';
import SolverTwoPanelLayout from './SolverTwoPanelLayout';
const getResultBoxClasses = (res) => {
  if (!res) return "bg-gray-50 border border-gray-200";
  if (res.result !== undefined && !res.error) return "bg-green-100 border border-green-300 text-green-800";
  return "bg-red-100 border border-red-300 text-red-800";
};

export default function QuadratureUI({ state = {}, setState }) {
  const {
    func = '',
    a = '',
    b = '',
    method = '',
    useSummedRule = false,
    subintervals = '20',
    loading = false,
    result = null,
    error = null,
  } = state;

  const set = (key, value) => setState((prev) => ({ ...prev, [key]: value }));

  const isValidSubintervals =
    subintervals !== '' &&
    Number.isInteger(Number(subintervals)) &&
    Number(subintervals) > 0;

  const isValidInput =
    func.trim() !== '' &&
    !isNaN(Number(a)) &&
    !isNaN(Number(b)) &&
    Number(a) !== Number(b) &&
    (!useSummedRule || isValidSubintervals);

  const handleIntegrate = async () => {
    set('loading', true);
    set('result', null);
    set('error', null);

    try {
      const response = await fetch('https://api.numsolve.org/quadrature', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          func,
          a: parseFloat(a),
          b: parseFloat(b),
          method,
          use_summed: useSummedRule,
          subintervals: useSummedRule ? parseInt(subintervals) : null,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        set('result', data);
        console.log("Received from backend:", data);

        try {
          const recentEntry = {
            timestamp: new Date().toISOString(),
            method: 'quadrature',
            input: {
              func,
              a: parseFloat(a),
              b: parseFloat(b),
              method,
              use_summed: useSummedRule,
              subintervals: useSummedRule ? parseInt(subintervals) : null,
            },
            // small log with sample points so export is not empty
            log: [
              {
                kind: 'summary',
                result: data.result ?? null,
                dx: data.dx ?? null,
                sample_points: Array.isArray(data.points)
                  ? data.points.slice(0, 10) // keep size reasonable
                  : null,
              },
            ],
          };
          const existing = JSON.parse(localStorage.getItem('calcLogs') || '[]');
          const updated = [recentEntry, ...existing].slice(0, 100);
          localStorage.setItem('calcLogs', JSON.stringify(updated));
          window.dispatchEvent(new Event('calcLogsUpdated'));
        } catch { }
      } else {
        set('error', data?.error || 'Unexpected error.');
      }
    } catch (err) {
      set('error', 'Backend is not reachable.');
    }

    set('loading', false);
  };

  console.log("Full result object:", result);
  const inputCard = (
    <div className="rounded-3xl border border-white/30 bg-white/80 p-8 shadow-[0_30px_80px_-45px_rgba(15,20,43,0.95)] backdrop-blur">
      <div className="space-y-4">
        <div>
          <label className="mb-2 block text-sm font-bold text-gray-700">Function f(x)</label>
          <div className="input-wrapper input-pulse-teal">
            <textarea
              value={func}
              onChange={(e) => set('func', e.target.value)}
              placeholder="e.g. x**3 - 3*x**2 + 5"
              rows={2}
              className="w-full resize-none rounded-xl border border-gray-300 bg-gray-50 px-4 py-2 text-gray-800 focus:outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input label="Lower Bound (a)" value={a} setValue={(val) => set('a', val)} placeholder="e.g. 0" type="number" />
          <Input label="Upper Bound (b)" value={b} setValue={(val) => set('b', val)} placeholder="e.g. 4" type="number" />
        </div>

        <DropdownSelect
          label="Quadrature Method"
          color="teal"
          value={method}
          onChange={(val) => set('method', val)}
          options={[
            { value: 'trapezoid', label: 'Trapezoid Rule' },
            { value: 'simpson', label: "Simpson's Rule" },
            { value: 'box', label: 'Midpoint (Box) Rule' },
          ]}
        />

        <div className="mt-2 flex items-center gap-2">
          <input
            type="checkbox"
            checked={useSummedRule}
            onChange={(e) => set('useSummedRule', e.target.checked)}
            className="h-4 w-4"
          />
          <label className="text-sm text-gray-600">Use composite rule</label>
        </div>

        {useSummedRule && (
          <div className="mt-2">
            <Input
              label="Number of subintervals"
              value={subintervals}
              setValue={(val) => set('subintervals', val)}
              placeholder="e.g. 20"
              type="number"
            />
            {subintervals !== '' && !isValidSubintervals && (
              <p className="mt-1 text-sm text-red-500">Must be a positive integer.</p>
            )}
          </div>
        )}

        <div className="mt-6">
          <button className="button button-teal" onClick={handleIntegrate} disabled={!isValidInput || loading}>
            {loading ? 'Integrating…' : 'Solve'}
          </button>
        </div>
      </div>
    </div>
  );

  const outputCard = (
    <div
      className={`rounded-3xl border border-white/30 bg-white/80 p-8 shadow-[0_30px_80px_-45px_rgba(15,20,43,0.95)] transition-all duration-300 ${
        result && result.result !== undefined && !result.error ? 'result-success' : ''
      }`}
    >
      <h2 className="mb-4 text-xl font-semibold text-gray-900">Results</h2>

      {result && (
        <div className="space-y-2 text-sm text-gray-700">
          {result.result !== undefined && (
            <p>
              <strong>Approximate Value:</strong> {result.result}
            </p>
          )}
          {result.error && (
            <p className="text-red-600">
              <strong>Error:</strong> {result.error}
            </p>
          )}
        </div>
      )}

      {!result && !error && !loading && (
        <div className="text-sm text-gray-500">No results yet. Please enter function and bounds.</div>
      )}

      {error && !result && (
        <div className="mt-4 rounded-lg bg-red-100 p-3 text-sm text-red-700">
          <strong>Error:</strong> {error}
        </div>
      )}

      {result?.plot && result.plot.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-md font-semibold text-gray-800">Function Plot</h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={result.plot}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="x"
                  type="number"
                  scale="linear"
                  tick={{ fontSize: 14 }}
                  axisId="x"
                  allowDataOverflow
                  domain={
                    a !== '' && b !== ''
                      ? (() => {
                          const aNum = parseFloat(a);
                          const bNum = parseFloat(b);
                          const min = Math.min(aNum, bNum);
                          const max = Math.max(aNum, bNum);
                          const pad = 0.05 * (max - min || 1);
                          return [min - pad, max + pad];
                        })()
                      : ['auto', 'auto']
                  }
                />
                <YAxis type="number" tick={{ fontSize: 14 }} />
                <Tooltip formatter={(value) => Number(value).toFixed(4)} labelFormatter={(value) => `x = ${Number(value).toFixed(4)}`} />

                <Line type="monotone" dataKey="y" stroke="#14b8a6" strokeWidth={2} dot={false} name="f(x)" />

                {result.trapezoids && result.trapezoids.length > 0 && (
                  <Line type="monotone" dataKey="top" data={result.trapezoids} stroke="#34d399" strokeWidth={1} dot={false} name="Trapezoids" />
                )}

                {result.boxes && result.boxes.length > 0 && (
                  <Line type="stepAfter" dataKey="height" data={result.boxes} stroke="#f97316" strokeWidth={1} dot={false} name="Midpoints" />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <SolverTwoPanelLayout
      loading={loading}
      inputCard={inputCard}
      outputCard={outputCard}
    />
  );
}

function Input({ label, value, setValue, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1 whitespace-pre">{label}</label>
      <div className="input-wrapper input-wrapper-tight input-pulse-teal">
        <input
          type={type}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          className="w-full px-4 py-2 rounded-xl border border-gray-300 bg-gray-50 text-gray-800 focus:outline-none"
        />
      </div>
    </div>
  );
}
